import type { Selection } from '@react-types/shared'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import Button from '@ui/Button/Button'
import Combobox from '@ui/Combobox'
import Tooltip from '@ui/Tooltip'
import { ChevronDownIcon } from '@ui/icons/tint/08'
import type { ThemeKey } from '@ui/theme'
import { ThemeProvider } from '@ui/theme'

import * as styles from './AudioDeviceCombobox.css'
import type { AudioDevice } from './useAudioDevice'

interface AudioDeviceComboboxProps {
  audioDevice: AudioDevice
  id?: string
  isDisabled?: boolean
  'aria-label'?: string
  themeKey?: ThemeKey
  onSelectionChange?: (deviceId: string) => void
}

function AudioDeviceCombobox({
  audioDevice,
  id,
  isDisabled,
  onSelectionChange,
  themeKey = 'dark',
  ...props
}: AudioDeviceComboboxProps) {
  const handleSelectionChange = (selection: Selection) => {
    const [key] = selection
    if (typeof key !== 'string') {
      return
    }
    onSelectionChange?.(key)
  }
  const isSupported = audioDevice.status.type === 'SUPPORTED'
  const isDisabledOrUnsupported = isDisabled || !isSupported
  return (
    <ThemeProvider themeKey={themeKey}>
      <Combobox.Provider>
        <Combobox.Trigger isDisabled={isDisabledOrUnsupported}>
          {({ props, state }) => (
            <Tooltip
              themeKey={themeKey}
              disabled={isSupported}
              placement="top left"
              title={audioDevice.status.message}
            >
              <Button
                {...props}
                color="default"
                disabled={isDisabledOrUnsupported}
                fullWidth
                icon={
                  <ChevronDownIcon
                    className={styles.triggerIcon({ isOpen: state.isOpen })}
                  />
                }
                iconPosition="far-right"
                id={id}
                key={audioDevice.activeDevice.deviceId}
                variant="outline"
              >
                <span className={cx(styles.truncate, styles.deviceLabel)}>
                  {audioDevice.activeDevice.label}
                </span>
              </Button>
            </Tooltip>
          )}
        </Combobox.Trigger>
        <Combobox.List
          width="100%"
          aria-label={props['aria-label']}
          items={audioDevice.devices}
          selectedKeys={[audioDevice.activeDevice.deviceId]}
          selectionMode="single"
          onSelectionChange={handleSelectionChange}
          closeOnSelect
        >
          {(device) => (
            <Combobox.ListItem key={device.deviceId} textValue={device.label}>
              {device.label}
            </Combobox.ListItem>
          )}
        </Combobox.List>
      </Combobox.Provider>
    </ThemeProvider>
  )
}

export default observer(AudioDeviceCombobox)
